import { useFetch } from "../useFetch";
import type {
	ReviewResponse,
	CreateReviewPayload,
	UpdateReviewPayload,
	ToggleReviewStatusPayload,
} from "../../core/types/services";
import { useMemo } from "react";

type PaginatedResponse<T> = {
	items: T[];
	meta: {
		total: number;
		page: number;
		pageSize: number;
		totalPages: number;
	};
};

// ============================================
// Tipos Auxiliares para Filtros
// ============================================
export type ReviewFilters = {
	page?: number;
	pageSize?: number;
	isActive?: boolean | string; // El backend lo transforma con @Type(() => Boolean)
};

// ============================================
// GET ALL REVIEWS (PAGINATED WITH FILTERS)
// ============================================
export function useReviews(filters: ReviewFilters = {}, auto: boolean = true) {
	const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

	// Estabilizamos el objeto query serializándolo para que NO cambie de referencia
	// en cada render si los valores internos son los mismos.
	const query = useMemo(() => {
		const parsedFilters = JSON.parse(filtersKey) as ReviewFilters;
		const { page = 1, pageSize = 10, ...rest } = parsedFilters;
		return { page, pageSize, ...rest };
	}, [filtersKey]);

	return useFetch<PaginatedResponse<ReviewResponse>>({
		endpoint: "/reviews",
		method: "GET",
		query, // Pasa los parámetros de búsqueda a la URL automáticamente
		auto, // Permite decidir si la petición se dispara sola o manualmente
	});
}

// ============================================
// GET REVIEW BY ID
// ============================================
export function useReview(id: number) {
	return useFetch<ReviewResponse>({
		endpoint: `/reviews/${id}`,
		method: "GET",
	});
}

// ============================================
// GET REVIEWS BY QUALIFIED USER (Perfil del proveedor/cliente)
// ============================================
export function useReviewsByQualified(qualifiedId: number) {
	return useFetch<ReviewResponse[]>({
		endpoint: `/reviews/qualified/${qualifiedId}`,
		method: "GET",
	});
}

// ============================================
// CREATE REVIEW
// ============================================
export function useCreateReview() {
	const fetch = useFetch<ReviewResponse, CreateReviewPayload>({
		endpoint: "/reviews",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateReviewPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE REVIEW (Content)
// ============================================
export function useUpdateReview(id: number) {
	const fetch = useFetch<ReviewResponse, UpdateReviewPayload>({
		endpoint: `/reviews/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (data: UpdateReviewPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, update };
}

// ============================================
// DEACTIVATE / ACTIVATE REVIEW
// ============================================
export function useDeactivateReview(id: number) {
	const fetch = useFetch<ReviewResponse, ToggleReviewStatusPayload>({
		endpoint: `/reviews/${id}/deactivate`, // Asumiendo que esta es la ruta
		method: "PATCH",
		auto: false,
	});

	const deactivate = (isActive: boolean) => {
		return fetch.execute({ body: { isActive } });
	};

	return { ...fetch, deactivate };
}
